










































































































































































































































































































































































































































































import { Component, Vue, Prop } from "vue-property-decorator";
import vesselFilesClient from "@/Scripts/clients/vessel-files-client";
import moment from "moment";
import authHeader from "@/services/AuthHeader";

interface VesselFile {
  id: number;
  fileName: string;
  fileSize: number;
  uploadedTime: string;
  uploadedBy: string;
  vesselId: number;
  binaryFileId: number;
}

interface Folder {
  folderName: string;
  folderPath: string;
  isTemp: boolean;
}

interface FileItem {
  type: "file";
  id: number;
  name: string;
  fullPath: string;
  fileSize: number;
  uploadedTime: string;
  uploadedBy: string;
  extension: string;
  data: VesselFile;
}

interface FolderItem {
  type: "folder";
  name: string;
  fullPath: string;
  isTemp: boolean;
}

type Item = FileItem | FolderItem;

@Component
export default class VesselFiles extends Vue {
  @Prop({ required: true }) vesselId!: number;
  @Prop({ required: true }) vesselName!: string;

  files: VesselFile[] = [];
  currentFolderPath = "";
  tempFolders: Folder[] = [];
  isFilesLoading = true;
  isUploading = false;
  errorMessage = "";
  sortBy = "name";
  sortDesc = false;

  showNewFolderDialog = false;
  showRenameDialogVisible = false;
  showDeleteDialogVisible = false;

  newFolderName = "";
  folderNameError = "";
  renameName = "";
  renameError = "";
  elementToRename: Item | null = null;
  elementToDelete: Item | null = null;

  vesselFileSizeLimitInMB = 1000;

  headers = [
    { text: "Name", value: "name", sortable: true },
    { text: "Uploaded", value: "uploadedTime", sortable: true },
    { text: "Uploaded by", value: "uploadedBy", sortable: true },
    { text: "File size", value: "fileSize", sortable: true },
    { text: "", value: "actions", sortable: false, align: "end" },
  ];

  async mounted(): Promise<void> {
    await this.loadFiles();
  }

  async loadFiles(): Promise<void> {
    this.isFilesLoading = true;
    try {
      const data = await vesselFilesClient.getAll(this.vesselId);
      this.files = data;
    } catch (error) {
      this.errorMessage = "Failed to load files";
      console.error(error);
    } finally {
      this.isFilesLoading = false;
    }
  }

  get breadcrumbItems(): any[] {
    const items: any[] = [];
    const pathParts = this.currentFolderPath.split("/").filter(p => p);

    items.push({
      text: this.vesselName,
      disabled: this.currentFolderPath === "",
      folder: { folderName: this.vesselName, folderPath: "" },
    });

    let accumulatedPath = "";
    pathParts.forEach(part => {
      accumulatedPath += part + "/";
      items.push({
        text: part,
        disabled: accumulatedPath === this.currentFolderPath,
        folder: { folderName: part, folderPath: accumulatedPath },
      });
    });

    return items;
  }

  get foldersInCurrentFolder(): FolderItem[] {
    const folderSet = new Set<string>();

    this.files.forEach(file => {
      if (file.fileName.startsWith(this.currentFolderPath)) {
        const relativePath = file.fileName.substring(this.currentFolderPath.length);
        const slashIndex = relativePath.indexOf("/");
        if (slashIndex > 0) {
          const folderName = relativePath.substring(0, slashIndex);
          folderSet.add(folderName);
        }
      }
    });

    const folders: FolderItem[] = Array.from(folderSet).map(name => ({
      type: "folder" as const,
      name,
      fullPath: this.currentFolderPath + name + "/",
      isTemp: false,
    }));

    // Add temp folders
    this.tempFolders.forEach(tempFolder => {
      if (tempFolder.folderPath === this.currentFolderPath + tempFolder.folderName + "/") {
        folders.push({
          type: "folder" as const,
          name: tempFolder.folderName,
          fullPath: tempFolder.folderPath,
          isTemp: true,
        });
      }
    });

    return folders;
  }

  get filesInCurrentFolder(): FileItem[] {
    return this.files
      .filter(file => {
        if (!file.fileName.startsWith(this.currentFolderPath)) return false;
        const relativePath = file.fileName.substring(this.currentFolderPath.length);
        return !relativePath.includes("/");
      })
      .map(file => {
        const fileName = file.fileName.split("/").pop() || "";
        const extension = fileName.split(".").pop() || "";
        return {
          type: "file" as const,
          id: file.id,
          name: fileName,
          fullPath: file.fileName,
          fileSize: file.fileSize,
          uploadedTime: file.uploadedTime,
          uploadedBy: file.uploadedBy,
          extension,
          data: file,
        };
      });
  }

  get rows(): Item[] {
    return [...this.foldersInCurrentFolder, ...this.filesInCurrentFolder];
  }

  get totalFileSize(): string {
    const bytes = this.files.reduce((sum, file) => sum + file.fileSize, 0);
    return this.formatFileSize(bytes);
  }

  get fileSizeLimit(): string {
    return this.formatFileSize(this.vesselFileSizeLimitInMB * 1000000);
  }

  get deleteConfirmText(): string {
    if (!this.elementToDelete) return "";
    return this.elementToDelete.type === "folder" ? "Are you sure you want to delete this folder and all of its contents?" : "Are you sure you want to delete this file?";
  }

  goToFolder(folder: { folderPath?: string; fullPath?: string }): void {
    this.currentFolderPath = folder.folderPath || folder.fullPath || "";
  }

  createNewFolder(): void {
    this.folderNameError = "";
    const folderName = this.newFolderName.trim();

    if (!folderName) {
      this.folderNameError = "Folder name is required";
      return;
    }

    const isDuplicate = this.foldersInCurrentFolder.some(f => f.name === folderName);
    if (isDuplicate) {
      this.folderNameError = `A folder with the name ${folderName} already exists`;
      return;
    }

    const folderPath = this.currentFolderPath + folderName + "/";
    this.tempFolders.push({ folderName, folderPath, isTemp: true });
    this.showNewFolderDialog = false;
    this.newFolderName = "";
  }

  showRenameDialog(item: Item): void {
    this.elementToRename = item;
    this.renameError = "";
    if (item.type === "file") {
      const nameWithoutExt = item.name.substring(0, item.name.lastIndexOf(".")) || item.name;
      this.renameName = nameWithoutExt;
    } else {
      this.renameName = item.name;
    }
    this.showRenameDialogVisible = true;
  }

  closeRenameDialog(): void {
    this.showRenameDialogVisible = false;
    this.renameName = "";
    this.renameError = "";
    this.elementToRename = null;
  }

  async renameElement(): Promise<void> {
    if (!this.elementToRename) return;

    this.renameError = "";
    const newName = this.renameName.trim();

    if (!newName) {
      this.renameError = "Name is required";
      return;
    }

    if (this.elementToRename.type === "file") {
      const fileItem = this.elementToRename;
      const newFileName = newName + "." + fileItem.extension;

      const isDuplicate = this.filesInCurrentFolder.some(f => f.id !== fileItem.id && f.name === newFileName);

      if (isDuplicate) {
        this.renameError = `A file with the name ${newFileName} already exists`;
        return;
      }

      const updatedFile = { ...fileItem.data };
      updatedFile.fileName = this.currentFolderPath + newFileName;

      try {
        await vesselFilesClient.update(updatedFile);
        await this.loadFiles();
        this.closeRenameDialog();
      } catch (error) {
        this.renameError = "Failed to rename file";
      }
    } else {
      const folderItem = this.elementToRename;
      const isDuplicate = this.foldersInCurrentFolder.some(f => f.fullPath !== folderItem.fullPath && f.name === newName);

      if (isDuplicate) {
        this.renameError = `A folder with the name ${newName} already exists`;
        return;
      }

      try {
        await this.renameFolder(folderItem, newName);
        this.closeRenameDialog();
      } catch (error) {
        this.renameError = "Failed to rename folder";
      }
    }
  }

  async renameFolder(folder: FolderItem, newName: string): Promise<void> {
    const newFolderPath = this.currentFolderPath + newName + "/";
    const filesToUpdate = this.files.filter(f => f.fileName.startsWith(folder.fullPath));

    // Handle temp folders (folders with no files yet)
    if (folder.isTemp) {
      const tempFolderIndex = this.tempFolders.findIndex(tf => tf.folderPath === folder.fullPath);
      if (tempFolderIndex !== -1) {
        this.tempFolders = this.tempFolders.map((tf, index) => (index === tempFolderIndex ? { folderName: newName, folderPath: newFolderPath, isTemp: true } : tf));
      }
      return;
    }

    try {
      for (const file of filesToUpdate) {
        const updatedFile = { ...file };
        updatedFile.fileName = file.fileName.replace(folder.fullPath, newFolderPath);
        await vesselFilesClient.update(updatedFile);
      }
      await this.loadFiles();
    } catch (error) {
      this.errorMessage = "Failed to rename folder";
      throw error;
    }
  }

  showDeleteDialog(item: Item): void {
    this.elementToDelete = item;
    this.showDeleteDialogVisible = true;
  }

  async deleteElement(): Promise<void> {
    if (!this.elementToDelete) return;

    if (this.elementToDelete.type === "folder") {
      await this.deleteFolder(this.elementToDelete);
    } else {
      await this.deleteFile(this.elementToDelete);
    }

    this.showDeleteDialogVisible = false;
  }

  async deleteFile(file: FileItem): Promise<void> {
    try {
      await vesselFilesClient.remove(file.id);
      await this.loadFiles();
    } catch (error) {
      this.errorMessage = "Failed to delete file";
    }
  }

  async deleteFolder(folder: FolderItem): Promise<void> {
    const filesToDelete = this.files.filter(f => f.fileName.startsWith(folder.fullPath));

    try {
      for (const file of filesToDelete) {
        await vesselFilesClient.remove(file.id);
      }
      this.tempFolders = this.tempFolders.filter(f => f.folderPath !== folder.fullPath);
      await this.loadFiles();
    } catch (error) {
      this.errorMessage = "Failed to delete folder";
    }
  }

  async handleFileUpload(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    this.errorMessage = "";
    this.isUploading = true;

    try {
      const uploadPromises = Array.from(input.files).map(file => this.uploadFile(file));
      await Promise.all(uploadPromises);
      await this.loadFiles();
    } catch (error) {
      this.errorMessage = "Failed to upload file(s)";
      console.error(error);
    } finally {
      this.isUploading = false;
      input.value = "";
    }
  }

  async uploadFile(file: File): Promise<void> {
    const fileName = this.currentFolderPath + file.name;
    const existingFile = this.filesInCurrentFolder.find(f => f.name === file.name);

    if (existingFile) {
      const confirm = window.confirm(`${file.name} already exists. Do you want to replace it?`);
      if (!confirm) return;
    }

    const formData = new FormData();
    // Create a new File with the full path as the filename
    const fileWithPath = new File([file], fileName, { type: file.type });
    formData.append("file", fileWithPath);

    const headers = {
      ...(authHeader() as Record<string, string>),
    };

    const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/VesselFiles/${this.vesselId}`, {
      method: "POST",
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Upload failed: ${response.statusText} - ${errorText}`);
    }

    // Remove temp folder if this was uploaded to it
    this.tempFolders = this.tempFolders.filter(f => !fileName.startsWith(f.folderPath));
  }

  viewPdf(file: FileItem): void {
    this.$emit("view-pdf", file.data);
  }

  downloadFile(file: FileItem): void {
    this.$emit("download-file", file.data);
  }

  formatDate(dateString: string): string {
    return moment(dateString).format("DD. MMM, YYYY HH:mm");
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  }

  getFileIcon(extension: string): string {
    const ext = extension.toLowerCase();
    const excel = ["xlsx", "xls", "xlt", "xlm", "xlsm", "xltx", "xltm"];
    const word = ["doc", "docx", "dot", "docm", "dotx", "dotm"];
    const pdf = ["pdf"];

    if (excel.includes(ext)) return "mdi-file-excel";
    if (word.includes(ext)) return "mdi-file-word";
    if (pdf.includes(ext)) return "mdi-file-pdf-box";
    return "mdi-file";
  }

  getFileIconColor(extension: string): string {
    const ext = extension.toLowerCase();
    const excel = ["xlsx", "xls", "xlt", "xlm", "xlsm", "xltx", "xltm"];
    const word = ["doc", "docx", "dot", "docm", "dotx", "dotm"];
    const pdf = ["pdf"];

    if (excel.includes(ext)) return "success";
    if (word.includes(ext)) return "info";
    if (pdf.includes(ext)) return "error";
    return "grey";
  }

  getFileCountInFolder(folderPath: string): string {
    const count = this.files.filter(file => file.fileName.startsWith(folderPath)).length;
    return count === 1 ? "1 document" : `${count} documents`;
  }
}
